import { Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import {
  ProductsTypeEnum,
  Question,
} from '@app/forms/subscription/definition/interface';
import {
  Owner,
  PledgeInitParams,
  PledgeRead,
} from '../definition/interface/Pledge';
import { DialogOwnersOverviewComponent } from '../subscription-overview/dialog-owners-overview/dialog-owners-overview.component';
import { PdfModalComponent } from '../subscription-pages/ea-subscription-final-page/pdf-modal/pdf-modal.component';
import { EntityService, SubmitionType } from './entity.service';
import { EventBusService } from './event-bus.service';
import { InMemoryDataService } from './in-memory-data.service';

import { FormGroup } from '@angular/forms';
import { CommandEnum } from '@app/forms/subscription/definition/interface/enum';
import { PledgeOptions } from '@app/forms/subscription/definition/interface/pledge-options';
import { DataLayerService } from '@app/services/data-layer.service';
import { WindowRefService } from '@app/services/window-ref.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { LoadingService } from './loading.service';
import { UnsubscriberService } from './unsubscriber.service';
import { reverseMapProductType } from './utile';

type Mode = 'create' | 'edit';
export interface DialogData {
  productType?: string;
  mode?: Mode;
  pledge?: PledgeRead;
}
interface DialogPdfData {
  signatureUrl: string;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionFacadeService {
  public readonly subscriptionRouteNavigation = '/dashboard/souscription';
  public readonly subscriptionRouteInit = '/dashboard/souscription/init';
  public readonly subscriptionRouteProduct = '/dashboard/souscription/product';

  public readonly dialogWidth = '400px';

  // PremiumType AE du devis (CI/CRD) — pas renseigné par l'API DI, c'est un choix du consultant
  aeDevisPremiumType: string | null = null;

  public readonly dialogPdfWidth = '100%';
  public readonly dialogPdfHeight = '800px';

  private showSidebarStepperSubject = new BehaviorSubject<boolean>(true);
  public showSidebarStepper$ = this.showSidebarStepperSubject.asObservable();

  setShowSidebarStepper(show: boolean): void {
    this.showSidebarStepperSubject.next(show);
  }

  private _eventBusService: EventBusService;
  public get eventBusService(): EventBusService {
    if (!this._eventBusService) {
      this._eventBusService = this.injector.get(EventBusService);
    }
    return this._eventBusService;
  }

  private _entityService: EntityService;
  public get entityService(): EntityService {
    if (!this._entityService) {
      this._entityService = this.injector.get(EntityService);
    }
    return this._entityService;
  }

  private _loadingService: LoadingService;
  public get loadingService(): LoadingService {
    if (!this._loadingService) {
      this._loadingService = this.injector.get(LoadingService);
    }
    return this._loadingService;
  }

  private _inMemoryDataService: InMemoryDataService;
  public get inMemoryDataService(): InMemoryDataService {
    if (!this._inMemoryDataService) {
      this._inMemoryDataService = this.injector.get(InMemoryDataService);
    }
    return this._inMemoryDataService;
  }

  private _unsubscriberService: UnsubscriberService;
  public get unsubscriberService(): UnsubscriberService {
    if (!this._unsubscriberService) {
      this._unsubscriberService = this.injector.get(UnsubscriberService);
    }
    return this._unsubscriberService;
  }

  public get takeUntilDestroy() {
    return this.unsubscriberService.takeUntilDestroy;
  }

  constructor(
    private injector: Injector,
    public router: Router,
    public route: ActivatedRoute,
    public dialog: MatDialog,
    private windowRef: WindowRefService,
    private dataLayer: DataLayerService,
  ) {}

  public loading() {
    return this.loadingService.loading();
  }

  public loaded() {
    this.loadingService.loaded();
  }
  public get loading$() {
    return this.loadingService.loading$;
  }
  public get error$() {
    return this.loadingService.error$;
  }

  public nextStep() {
    this.eventBusService.nextStep();
  }

  public previousStep() {
    this.eventBusService.previousStep();
    this.clearStepNameAndCommands();
  }

  public clearStepNameAndCommands() {
    this.entityService.clearStepNameAndCommands();
  }

  public clearStep() {
    this.eventBusService.clearStep();
  }

  public setStep(step: number) {
    this.eventBusService.setStep(step);
  }

  public getStep() {
    return this.eventBusService.getStep();
  }

  public get stepChanged$() {
    return this.eventBusService.stepChanged$;
  }

  public setOwner(entity: Owner[]) {
    this.entityService.setOwner(entity);
  }

  public getEntity() {
    return this.entityService.getEntity();
  }

  public setCommand(command: CommandEnum[]) {
    this.entityService.setCommand(command);
  }
  public setStepName(stepName: string) {
    if (!this.entityService.getUser().sessionConsultant) {
      this.windowRef.nativeWindow.productName = this.dataLayer.getProductName(
        this.router.url,
      );
      this.windowRef.nativeWindow.stepName = stepName;
      this.dataLayer.getStepName(stepName, this.router.url);
    }

    this.entityService.setStepName(stepName);
  }
  public getStepName() {
    return this.entityService.getStepName();
  }

  public setPledge(pledge: PledgeRead) {
    this.entityService.setPledge(pledge);
  }

  public getPledge() {
    return this.entityService.getPledge();
  }
  public getPladge$() {
    return this.entityService.getPladge$();
  }
  public emitPledgeAndEntity(pledge: PledgeRead) {
    this.entityService.emitPledgeAndEntity(pledge);
  }
  public emitPledge(pledge: PledgeRead) {
    this.entityService.emitJustPledge(pledge);
  }

  public get pledgeChanged$() {
    return this.entityService.pledgeChanged$;
  }

  public get ownerChanged$() {
    return this.entityService.ownerChanged$;
  }
  setQuestions(questions: Question[]) {
    this.entityService.setQuestions(questions);
  }
  public getQuestions() {
    return this.entityService.getQuestions();
  }
  setQuestion(question: Question) {
    this.entityService.setQuestion(question);
  }
  getUser(): UserAuthentified {
    return this.entityService.getUser();
  }
  setUser(user: UserAuthentified) {
    this.entityService.setUser(user);
  }
  getPledgeOptions(): PledgeOptions {
    return this.entityService.getPledgeOptions();
  }
  setPledgeOptions(pledgeOptions: PledgeOptions) {
    this.entityService.setPledgeOptions(pledgeOptions);
  }
  getPledgeOptions$(): Observable<PledgeOptions> {
    return this.entityService.getPledgeOptions$();
  }
  public getQuestion() {
    return this.entityService.getQuestion();
  }
  public getIsProgress() {
    return this.entityService.getIsProgress();
  }
  setIsProgress(value: boolean) {
    return this.entityService.setIsProgress(value);
  }
  public get iisProgressChanged$() {
    return this.entityService.iisProgressChanged$;
  }
  public get questionsChanged$() {
    return this.entityService.questionsChanged$;
  }
  public get questionChanged$() {
    return this.entityService.questionChanged$;
  }
  public get clickSubject$() {
    return this.entityService.clickSubject$;
  }
  public notifyClick() {
    this.entityService.notifyClick();
  }

  public savePledge(owner: Owner | any) {
    this.entityService.savePledge(owner);
  }
  public putPledge(pledge: PledgeRead) {
    return this.entityService.putPledge(pledge);
  }
  public selectErrorInputAndScroll(prForm: PrFormAbstract) {
    prForm.displayErrors();
    setTimeout(() => {
      let firstErrorField = document.querySelector('.is-invalid');
      if (!firstErrorField) return;
      const element = document.querySelector('.is-invalid').parentElement;
      element.scrollIntoView();
      const firstErrorFieldInput = firstErrorField
        .children[0] as HTMLInputElement;
      firstErrorFieldInput.select();
    }, 0);
  }
  public submitPledgeWithoutNextStep(
    prForm: PrFormAbstract,
    type: SubmitionType = 'pledge',
    callback: () => any = undefined,
  ) {
    this.submitPledge(prForm, type, false, callback, true);
  }
  public submitPledge(
    prForm: PrFormAbstract,
    type: SubmitionType = 'pledge',
    withNextStep: boolean = true,
    callback: () => any = undefined,
    formValid: boolean = false,
  ) {
    this.entityService.submitPledge(
      prForm,
      type,
      withNextStep,
      callback,
      formValid,
    );
  }
  public submitPledgeWithoutForm(prForm: any, type: SubmitionType = 'pledge') {
    this._entityService.submitPledgeWithoutForm(prForm, type);
  }

  public getPledges$() {
    return this.entityService.getPledges$;
  }

  public get pledgesWithCRUD$() {
    return this.entityService.pledgesWithCRUD$;
  }

  public initPledge(params: PledgeInitParams) {
    return this.entityService.initPledge(params);
  }
  public deletePledge(pledge: PledgeRead) {
    return this.entityService.deletePledge(pledge);
  }

  public initForm(prForm: PrFormAbstract, option?: any) {
    this.entityService.initForm(prForm, option);
  }
  public initFormWithDocuments(prForm: PrFormAbstract, type?: any) {
    return this.entityService.initFormWithDocuments(prForm, type);
  }

  public clearEntity() {
    this.entityService.clearEntity();
  }
  public clearPledge() {
    this.entityService.clearPledge();
  }

  public getProductQuestionsByName(name: string) {
    return this.inMemoryDataService.getProductQuestionsByName(name);
  }

  public getConsultantQuestions({
    product,
    entity,
    user = undefined,
  }: {
    product: string;
    entity;
    user: UserAuthentified;
  }) {
    return this.inMemoryDataService.getConsultantQuestions({
      product,
      entity,
      user,
    });
  }
  public getConsultantQuestionsFirstTime({
    product,
    entity,
    user = undefined,
  }: {
    product: string;
    entity;
    user: UserAuthentified;
  }) {
    return this.inMemoryDataService.getConsultantQuestionsFirstTime({
      product,
      entity,
      user,
    });
  }
  public getClientQuestions({ product, entity }: { product: string; entity }) {
    return this.inMemoryDataService.getClientQuestions({ product, entity });
  }
  public getQuestionByStep(step: string) {
    return this.inMemoryDataService.getQuestionByStep(step);
  }
  public getQuestionsByCategory(category: string) {
    return this.inMemoryDataService.getQuestionsByCategory(category);
  }
  public getMenu() {
    return this.inMemoryDataService.getMenu();
  }

  public navigateTo(
    productType: string,
    pledgeId: number,
    ownerIds: (string | number)[],
    url = this.subscriptionRouteNavigation,
    additionalQueryParams: Record<string, string | number> = {},
  ) {
    this.router.navigate([url, productType], {
      relativeTo: this.route,
      queryParams: {
        ids: ownerIds?.join(','),
        pledgeId,
        ...additionalQueryParams,
      },
      state: {
        ids: ownerIds,
      },
    });
  }
  public navigateToProduct(
    productType: string,

    ownerIds: (string | number)[],
    url = this.subscriptionRouteProduct,
  ) {
    this.router.navigate([url], {
      relativeTo: this.route,
      queryParams: {
        productType,
        ids: ownerIds?.join(','),
      },
      state: {
        ids: ownerIds,
      },
    });
  }
  openDialog(data: DialogData): void {
    this.dialog.open(DialogOwnersOverviewComponent, {
      width: this.dialogWidth,

      data: { ...data },
    });
  }
  openPdfDialog(data: DialogPdfData): void {
    this.dialog.open(PdfModalComponent, {
      width: this.dialogPdfWidth,
      // height: this.dialogPdfHeight,
      data: { ...data },
    });
  }
  initOwner(pledge: PledgeRead) {
    this.emitPledgeAndEntity(pledge);
    const ownerIds = pledge.owners.map(owner => owner.id);
    let productType: any = pledge.productType as keyof typeof ProductsTypeEnum;
    productType = reverseMapProductType(productType);

    this.navigateTo(productType, pledge.id, ownerIds);
  }
  createLink(
    resp,
    name,
    blobPropertyBag: BlobPropertyBag = { type: 'application/pdf' },
  ) {
    let blob = new Blob([resp], { ...blobPropertyBag });
    let link = document.createElement('a');
    document.body.appendChild(link);
    link.href = window.URL.createObjectURL(blob);
    link.download = `${name}.pdf`;
    link.click();
    document.body.removeChild(link);
  }
  setupFormValueAmountChanges(
    prForm: FormGroup,
    fields: { investmentAmount: string; taxReductionAmount: string },
    sessionConsultant: boolean,
  ) {
    return this.entityService.setupFormValueAmountChanges(
      prForm,
      fields,
      sessionConsultant,
    );
  }
}
